import { Link } from 'react-router-dom'
import { Heart, MessageCircle, Clock, Eye } from 'lucide-react'
import { cn } from '@/utils/cn'

interface Post {
  id: string
  slug: string
  title: string
  excerpt: string
  content: string
  author: {
    name: string
    avatar: string
  }
  category: string
  tags: string[]
  image: string
  publishedAt: string
  readTime: number
  likes: number
  comments: number
  views: number
}

interface PostCardProps {
  post: Post
  className?: string
}

export function PostCard({ post, className }: PostCardProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))

    if (diffInHours < 1) return 'Agora há pouco'
    if (diffInHours < 24) return `Há ${diffInHours}h`
    if (diffInHours < 48) return 'Ontem'

    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit'
    })
  }

  return (
    <Link
      to={`/blog/${post.slug}`}
      className={cn(
        "block card hover:shadow-md transition-all duration-200 group overflow-hidden",
        className
      )}
    >
      <article>
        {/* Imagem */}
        <div className="bg-brand-surface-alt h-48 overflow-hidden relative">
          <img
            src={post.image}
            alt={post.title}
            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              e.currentTarget.src = `https://picsum.photos/seed/${post.slug}/400/225.jpg`
            }}
          />
          {/* Gradiente overlay para melhor legibilidade do texto */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>

        {/* Conteúdo */}
        <div className="p-4">
          {/* Categoria e Data */}
          <div className="flex items-center justify-between mb-3">
            <span className="inline-block px-2.5 py-1 text-xs font-medium bg-brand-primary-light text-brand-primary rounded-lg">
              {post.category}
            </span>
            <time className="text-xs text-brand-text-muted flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatDate(post.publishedAt)}
            </time>
          </div>

          {/* Título */}
          <h3 className="text-base font-semibold text-brand-text mb-2 line-clamp-2 group-hover:text-brand-primary transition-colors">
            {post.title}
          </h3>

          {/* Excerpt */}
          <p className="text-brand-text-secondary text-sm mb-4 line-clamp-3">
            {post.excerpt}
          </p>

          {/* Meta informações */}
          <div className="flex items-center justify-between text-xs text-brand-text-muted">
            {/* Autor */}
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-brand-surface-alt rounded-full overflow-hidden flex-shrink-0">
                <img
                  src={post.author.avatar}
                  alt={post.author.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(post.author.name)}&background=00342f&color=ffffff&size=20`
                  }}
                />
              </div>
              <span className="truncate">{post.author.name}</span>
            </div>

            {/* Tempo de leitura */}
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {post.readTime} min
            </span>
          </div>

          {/* Estatísticas */}
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-brand-border">
            <div className="flex items-center gap-4 text-xs text-brand-text-muted">
              <span className="flex items-center gap-1">
                <Heart className="w-3 h-3" />
                {post.likes}
              </span>
              <span className="flex items-center gap-1">
                <MessageCircle className="w-3 h-3" />
                {post.comments}
              </span>
              <span className="flex items-center gap-1">
                <Eye className="w-3 h-3" />
                {post.views}
              </span>
            </div>

            {/* Tags (limitado) */}
            <div className="flex gap-1">
              {post.tags.slice(0, 2).map(tag => (
                <span
                  key={tag}
                  className="px-1.5 py-0.5 text-xs bg-brand-surface-alt text-brand-text-muted rounded-md"
                >
                  #{tag}
                </span>
              ))}
              {post.tags.length > 2 && (
                <span className="px-1.5 py-0.5 text-xs bg-brand-surface-alt text-brand-text-muted rounded-md">
                  +{post.tags.length - 2}
                </span>
              )}
            </div>
          </div>
        </div>
      </article>
    </Link>
  )
}